import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Button, InlineLoading, InlineNotification } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import type { StagedWell } from 'types/well-workspace';
import { visibleCardsAtom } from '../atoms';
import { wellDisplayName } from '../utils';
import {
  usePromoteWorkspace,
  useWellStatusVerdict,
  useWorkspace,
  useOperatorOptions,
  useIdentityAutosave,
  useStatusAutosave,
} from '../hooks';
import { WorkspaceHeader } from './workspace-header';
import { WorkspaceRail } from './workspace-rail';
import { treeOrder } from './well-tree-utils';
import { WellDetailsCard } from './well-details-card';
import { WellStatusCard } from './well-status-card';
import {
  WellStatusVerdictPanel,
  type VerdictKind,
} from './well-status-verdict-panel';
import { WellEurCard } from './well-eur-card';
import { RelatedDocumentsCard } from './related-documents-card';
import { useScopedWell } from './use-scoped-well';
import {
  PermitDetailsCard,
  ProductionDetailsCard,
  StimulationsCard,
  SubsurfaceExplorerCard,
  UnitDetailsCard,
  WellboreCard,
  WellboreCompletionsCard,
  WellMapCard,
} from './well-section-cards';
import { DETAIL_CARDS } from './section-cards';
import {
  CardSlot,
  isCardVisible,
  useDockOffsets,
  type WellScope,
} from 'components/workspace-cards';
import { EventsPanel } from 'components/events-panel';
import { isEventsPanelOpenAtom } from 'components/events-panel/atoms';
import { VerificationTasksPanel } from 'components/verification-tasks-panel';
import {
  WellsColorRulesProvider,
  resolveTraceColors,
  workspaceColorRulesAtom,
} from 'components/wells-color-by';
import { CompareSheet } from './compare-sheet';
import { ManageWellsTearsheet } from './manage-wells-tearsheet';
import { SuccessToast } from 'components/success-toast';
import styles from './workspace-shell.module.scss';

/**
 * What the Tasks panel says while the backend reports a count but lists no items.
 *
 * Says the count back, because the header is showing it: telling a reader there are no
 * tasks under a button reading "Tasks 1/4" would just look broken.
 * @param {{ completed: number, total: number }} tasks The envelope's counts.
 * @return {string} The panel's empty message.
 */
const tasksMessage = (tasks: { completed: number; total: number }): string => {
  if (tasks.total === 0) {
    return 'No verification tasks yet — they arrive with well verification.';
  }

  return `${tasks.completed} of ${tasks.total} verification tasks complete. The individual tasks are not available to show yet.`;
};

interface ShellProps {
  /** The workspace to show. */
  id: string;
  /** Leaves the workspace — clears the host's atom, or navigates for the route. */
  onLeave: () => void;
  /** What Back should say, since where it goes depends on the host. */
  backLabel: string;
  /**
   * The well the reader arrived from, when the host is a well's page. Scopes the
   * workspace to that well on arrival instead of its first well — a workspace opened
   * from SEMINOLE's page should land on SEMINOLE, not on whichever well sorts first.
   * Ignored when the workspace does not hold it; omitted by the listing route.
   */
  initialUwi?: string | null;
}

/**
 * One Well Workspace.
 *
 * Takes the workspace id and a way out rather than reading the route, as
 * `AreaWorkspaceShell` and `PreDrillWorkspaceShell` do; `WellWorkspacePage` reads the
 * `/well_workspaces/:id` route and hands both over.
 * @param {ShellProps} props The workspace id and the leave handler.
 * @return {JSX.Element} The workspace.
 */
export const WellWorkspaceShell: FC<ShellProps> = ({
  id,
  onLeave,
  backLabel,
  initialUwi = null,
}) => {
  const [selectedUwi, setSelectedUwi] = useState<string | null>(null);

  const [compareOpen, setCompareOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  // Which verdict the decision panel is capturing a reason for; null while it is closed.
  const [verdictKind, setVerdictKind] = useState<VerdictKind | null>(null);
  const [promoteConfirmed, setPromoteConfirmed] = useState(false);
  const [visibleCards, setVisibleCards] = useAtom(visibleCardsAtom);
  // Read-only here: the rail writes them, the Map card reads what they resolve to.
  const colorRules = useAtomValue(workspaceColorRulesAtom);

  // The read. React Query cancels a superseded fetch, retries once, and caches the
  // result, so navigating back to a workspace paints from cache while it revalidates.
  const {
    data: detail,
    isPending: loading,
    isError: loadFailed,
  } = useWorkspace(id);

  // The writes. Each invalidates the detail on success, so nothing refetches by hand —
  // and because their errors are separate from the read's, a write that succeeded and
  // then failed to refresh no longer reports itself as a failed write.
  const promotion = usePromoteWorkspace(id, () => setPromoteConfirmed(true));
  const promoteBusy = promotion.busy;
  const verdictSetter = useWellStatusVerdict(id);

  const setEventsPanelOpen = useSetAtom(isEventsPanelOpenAtom);
  // One right-hand drawer at a time — the Tasks panel and the Comments panel occupy the
  // same edge, and two open at once would stack on top of each other. (Well Status used
  // to be a third; it is edited on the card now and opens nothing.)
  const openVerdictPanel = useCallback(
    (kind: VerdictKind) => {
      setTasksOpen(false);
      setEventsPanelOpen(false);
      setVerdictKind(kind);
    },
    [setEventsPanelOpen]
  );

  // A verdict is posted by the shared verification control, not through React Query, so
  // the workspace is re-read by hand to pick the new verdict (and any freeze) back up.
  // Bleed the workspace to the shell edges and bound it to the viewport (the shared
  // #main-content carries the 32px padding every other page wants). Scoped by a class so
  // nothing else changes, and removed on unmount.
  useEffect(() => {
    const shell = document.getElementById('main-content');
    shell?.classList.add('well-workspace-fullbleed');
    return () => shell?.classList.remove('well-workspace-fullbleed');
  }, []);

  const wells = useMemo(() => detail?.wells ?? [], [detail]);

  // The scoped well, derived in one pass so there is no frame on the first well before
  // the one the reader came from takes over: the well they picked while it is still in
  // the workspace, else the one the host named on arrival, else the workspace's first.
  const selectedWell: StagedWell | undefined = useMemo(
    () =>
      wells.find((w) => w.uwi === selectedUwi) ??
      wells.find((w) => w.uwi === initialUwi) ??
      wells[0],
    [wells, selectedUwi, initialUwi]
  );
  /* The Well Status card's own editing. Mounted here rather than inside the card because
     the card is remounted by the layout rail; a pending save lives on the page. */
  const statusAutosave = useStatusAutosave(
    id,
    selectedWell,
    !(detail?.promoted ?? false)
  );
  /* The identity half, on the Well Details card. Its own hook rather than a flag on the
     status one: a different mutation, a different address, and no cross-field rules. */
  const identityAutosave = useIdentityAutosave(
    id,
    selectedWell,
    !(detail?.promoted ?? false)
  );
  // Only read while something can actually be edited, so a promoted workspace asks the
  // well index for nothing.
  const operatorOptions = useOperatorOptions(!(detail?.promoted ?? false));

  // Stepped in the order the tree draws, not the order the backend returned, so Next
  // always moves to the row under the highlight.
  const ordered = useMemo(() => treeOrder(wells), [wells]);
  const selectedIndex = useMemo(
    () => ordered.findIndex((w) => w.uwi === selectedWell?.uwi),
    [ordered, selectedWell]
  );

  const step = (delta: number) => {
    const next = ordered[selectedIndex + delta];
    if (next) setSelectedUwi(next.uwi);
  };

  // One scope object shared by every card. The mock repeats the stepper in each card
  // header, but there is a single scoped well — stepping in any header moves them all.
  const scope: WellScope = useMemo(
    () => ({
      wellName: selectedWell ? wellDisplayName(selectedWell) : '',
      onPrevious: () => step(-1),
      onNext: () => step(1),
      hasPrevious: selectedIndex > 0,
      hasNext: selectedIndex < ordered.length - 1,
    }),
    // `step` closes over the current index and the ordered wells, so it is rebuilt with
    // them rather than listed. `ordered` itself, not its length: a refetch that reorders
    // the wells must not leave the stepper walking the old order.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedWell?.name, selectedIndex, ordered]
  );

  /* A well authored here has no canonical twin, so `wellId` is null until it promotes.
     Resolved before the scoped read because that read needs it too: an authored well's
     units come from the workspace's own endpoint rather than the canonical analysis. */
  const stagedWellId = selectedWell?.id ?? null;
  const authored = selectedWell?.wellId === null;

  // The canonical well + unit intersection behind the scoped staged well, fetched once
  // and shared by every hosted production section.
  const scoped = useScopedWell(
    selectedWell?.uwi,
    Number(id),
    authored,
    stagedWellId
  );
  // `workspaceId` rides along to every section that reads the wells query, so a
  // promoted workspace's rows resolve as-of promotion and a draft's carry its staged
  // edits (INS-12130). `id` is the route param, hence the coercion.
  const sectionProps = useMemo(
    () => ({
      uwi: selectedWell?.uwi ?? '',
      // The sections that read a canonical well use these to reach the workspace's own
      // endpoints instead, which are keyed by the staged row rather than by uwi. Hoisted
      // above, where the scoped read also needs them.
      stagedWellId,
      authored,
      /* The bore the well derives from, straight off the staged row the page already
         holds — the backend serves it on the page read now, so nothing re-fetches and
         nothing derives it. UNDEFINED rather than null for a well not authored here,
         where the backend renders no bore block at all; the Wellbore card withholds the
         primary column on undefined and shows a boreless well on null. */
      primaryCompletionUwi14: selectedWell?.primaryCompletionUwi14,
      boreCompletionUwi14s: selectedWell?.boreCompletionUwi14s,
      scoped,
      scope,
      workspaceId: Number(id),
    }),
    [selectedWell, scoped, scope, id, authored, stagedWellId]
  );

  // What the rail's colour rules say the scoped well should draw in. Resolved here
  // because the rules are written over the STAGED well's values, and the Map card is
  // handed the canonical one — which carries neither county nor the landing zones.
  const mapTraceColors = useMemo(
    () =>
      resolveTraceColors(colorRules, 'map', {
        status: selectedWell?.status ?? null,
        holeDirection: selectedWell?.holeDirection ?? null,
        currentOperator: selectedWell?.currentOperator ?? null,
        stateAbbreviation: selectedWell?.stateAbbreviation ?? null,
        county: selectedWell?.county ?? null,
        srpLandingZone: selectedWell?.srpLandingZone ?? null,
        vendorLandingZone: selectedWell?.vendorLandingZone ?? null,
        uwi: selectedWell?.uwi ?? null,
      }),
    [colorRules, selectedWell]
  );

  // Which cards are docked to the top. Any card can be pinned; the pinned ones tile
  // down from the top of the column in page order while the rest scroll underneath.
  // Session state, like the mock — nothing is persisted.
  const [pinnedCards, setPinnedCards] = useState<Set<string>>(new Set());
  const togglePinnedCard = useCallback((sectionId: string) => {
    setPinnedCards((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);
  // Where each docked card sticks — the summed height of the ones docked above it.
  // Re-measured when a card is hidden as well as when one is pinned: hiding a pinned
  // card drops it from the dock without touching `pinnedCards`.
  const dockOffsets = useDockOffsets(pinnedCards, JSON.stringify(visibleCards));

  // Clearing a verdict captures no reason, so it skips the panel and posts straight away —
  // the same shape the Inventory workspace's header uses.
  const clearVerdict = useCallback(() => {
    const itemId = selectedWell?.wellStatusVerdict?.verificationItemId;
    if (itemId)
      verdictSetter.set({ verificationItemId: itemId, verdict: 'unanswered' });
    // `verdictSetter` is rebuilt every render by useMutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWell?.wellStatusVerdict?.verificationItemId]);

  const handlePromote = () => {
    if (!detail) return;
    setPromoteConfirmed(false);
    promotion.promote();
  };

  // A refusal stays inline where the reader is working; the confirmation is a toast in
  // the top-right corner, as the other pages report a save, so it does not push the
  // workspace down and clears itself.
  const dismissBanner = () => promotion.reset();

  if (loading)
    return (
      <InlineLoading
        description="Loading workspace…"
        className={styles.loading}
      />
    );
  if (loadFailed || !detail)
    return (
      <InlineNotification
        kind="error"
        title="Error"
        subtitle={loadFailed ? 'Failed to load workspace' : 'Not found'}
        lowContrast
      />
    );

  return (
    // Publishes this workspace's rules to the Subsurface Explorer card below. Its own
    // atom, so the Well page's rules never leak in here or the other way.
    <WellsColorRulesProvider rules={colorRules}>
      <div className={styles.page}>
        <WorkspaceHeader
          workspace={detail}
          wellCount={wells.length}
          promoteBusy={promoteBusy}
          onBack={onLeave}
          backLabel={backLabel}
          onCompare={() => setCompareOpen(true)}
          onOpenTasks={() => setTasksOpen(true)}
          onPromote={handlePromote}
        />

        {promotion.error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={promotion.error}
            lowContrast
            onCloseButtonClick={dismissBanner}
          />
        )}
        {promoteConfirmed && (
          <SuccessToast
            message="Workspace promoted"
            subtitle="Its wells are now the canonical records."
            onClose={() => {
              setPromoteConfirmed(false);
              return true;
            }}
          />
        )}

        <div className={styles.body}>
          <WorkspaceRail
            wells={wells}
            selectedUwi={selectedWell?.uwi ?? null}
            onSelectWell={setSelectedUwi}
            visibleCards={visibleCards}
            onToggleCard={(cardId, visible) =>
              setVisibleCards((prev) => ({ ...prev, [cardId]: visible }))
            }
            // A promoted workspace's working set is frozen, so it gets no control at
            // all — neither prop, so the "+" is absent rather than disabled. A draft
            // gets the Add or Remove Wells sheet (Epic 3, Story 3).
            onManageWells={
              detail.promoted ? undefined : () => setManageOpen(true)
            }
          />

          {/* Main: the section cards, each carrying its own well stepper */}
          {/* Identified so the Well Status panel has something to slide in beside: the
            shared #main-content wraps the whole shell, and pushing that would take the
            dark workspace bar and the rail with it. */}
          <main id="well-workspace-main" className={styles.main}>
            {selectedWell ? (
              DETAIL_CARDS.map((card) => (
                <CardSlot
                  key={card.id}
                  sectionId={card.id}
                  pinnedIds={pinnedCards}
                  onTogglePin={togglePinnedCard}
                  stickyTop={dockOffsets[card.id] ?? 0}
                  // Hidden, not unmounted: unmounting is unsafe for the Map and
                  // wasteful for the Explorer (see CardSlot's `hidden`), and hiding
                  // hands the card back with its charts and paging as they were.
                  hidden={!isCardVisible(visibleCards, card.id)}
                >
                  {renderCard(card)}
                </CardSlot>
              ))
            ) : (
              /* An empty workspace is a legitimate state (Epic 3 Story 3 — removing the
                 last well is allowed), so a draft offers the way back out of it rather
                 than leaving the reader on a dead end. A promoted workspace's membership
                 is frozen, so it gets the bare sentence. */
              <p className={styles.empty}>
                No wells in this workspace.{' '}
                {!detail.promoted && (
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={Add}
                    onClick={() => setManageOpen(true)}
                  >
                    Add wells
                  </Button>
                )}
              </p>
            )}
          </main>
        </div>

        <CompareSheet
          open={compareOpen}
          workspace={detail}
          selectedUwi={selectedWell?.uwi ?? null}
          onClose={() => setCompareOpen(false)}
        />

        {/* Add / remove members (Epic 3, Story 3). Mounted only on a draft — a promoted
          workspace withholds the rail control that opens it. */}
        {!detail.promoted && (
          <ManageWellsTearsheet
            open={manageOpen}
            workspace={detail}
            onClose={() => setManageOpen(false)}
          />
        )}

        <WellStatusVerdictPanel
          kind={verdictKind}
          well={selectedWell}
          setter={verdictSetter}
          onClose={() => setVerdictKind(null)}
        />

        {/* Mounted only while open, so an empty panel is not sitting in the page's text.
          The count is real — a workspace can report 1/4 — but the envelope carries no
          items and `resources` advertises no task endpoint, so there is nothing to list.
          The message therefore states the count rather than claiming there are no tasks,
          which would contradict the header. It is the panel the type-curve workspace
          uses, so it fills in as soon as the backend sends items. */}
        {tasksOpen && (
          <VerificationTasksPanel
            tasks={[]}
            title="Tasks"
            groupLabel="Well Verification"
            emptyMessage={tasksMessage(detail.tasks)}
            progress={detail.tasks}
            onClose={() => setTasksOpen(false)}
          />
        )}

        {/* The bar's comment button only flips isEventsPanelOpenAtom; without a panel
          mounted here it would have nothing to open. Hrefs come off the workspace's own
          `resources` rather than a hand-built path, which is why the backend advertises
          them (INS-10338). */}
        <EventsPanel
          header={detail.name}
          title="Workspace"
          helperText="Workspace context allows you to share information about the wells in this workspace."
          eventsHref={detail.resources?.events?.href}
          commentsHref={detail.resources?.comments?.href}
        />
      </div>
    </WellsColorRulesProvider>
  );

  // Declared after the render so the early returns above stay where they are; the
  // guard restates what those already guarantee, for the type checker's benefit.
  function renderCard(card: (typeof DETAIL_CARDS)[number]) {
    if (!selectedWell || !detail) return null;
    {
      if (card.id === 'well-details')
        return (
          <WellDetailsCard
            key={card.id}
            well={selectedWell}
            scope={scope}
            // Frozen once promoted, and unaddressable without a staged row — the edit is
            // keyed by the staged-well id, which a member-only snapshot does not carry.
            // Both conditions live in the hook's own `editable`.
            autosave={identityAutosave}
            operators={operatorOptions}
          />
        );
      if (card.id === 'well-status')
        return (
          <WellStatusCard
            key={card.id}
            well={selectedWell}
            scope={scope}
            promoted={detail.promoted}
            autosave={statusAutosave}
            onSetVerdict={openVerdictPanel}
            onClearVerdict={clearVerdict}
            verdictBusy={verdictSetter.saving}
          />
        );
      if (card.id === 'eur')
        return (
          <WellEurCard key={card.id} eur={scoped.well?.eur} scope={scope} />
        );
      // The production sections, hosted from `well-page-content/*` scoped to
      // the selected well — the same components the Well page renders.
      if (card.id === 'map')
        return (
          <WellMapCard
            key={card.id}
            {...sectionProps}
            traceColors={mapTraceColors}
          />
        );
      if (card.id === 'permit-details')
        return <PermitDetailsCard key={card.id} {...sectionProps} />;
      if (card.id === 'unit-details')
        return <UnitDetailsCard key={card.id} {...sectionProps} />;
      if (card.id === 'wellbore-geometry')
        return <WellboreCard key={card.id} {...sectionProps} />;
      if (card.id === 'wellbore-completions')
        return <WellboreCompletionsCard key={card.id} {...sectionProps} />;
      if (card.id === 'subsurface-explorer')
        return <SubsurfaceExplorerCard key={card.id} {...sectionProps} />;
      if (card.id === 'stimulation-details')
        return <StimulationsCard key={card.id} {...sectionProps} />;
      if (card.id === 'production-details')
        return <ProductionDetailsCard key={card.id} {...sectionProps} />;
      if (card.id === 'related-well-files')
        return (
          <RelatedDocumentsCard
            key={card.id}
            workspaceId={detail.id}
            uwi={selectedWell.uwi}
            promoted={detail.promoted}
            scope={scope}
          />
        );
      // Every id in DETAIL_CARDS is handled above; this is the type-checker's exit.
      return null;
    }
  }
};
